// ── Konfiguration ─────────────────────────────────────────

const STOREFRONT_TOKEN = document
  .getElementById('mass-entry-app')
  ?.dataset?.storefrontToken;

const SHOP_DOMAIN    = window.Shopify.shop;
const STOREFRONT_URL = `https://${SHOP_DOMAIN}/api/2026-04/graphql.json`;

const GAME_TAGS = {
  'Magic: The Gathering': 'mtg',
  'Yu-Gi-Oh!':            'ygo',
  'One Piece':            'opcg',
  'Pokémon':              'pkm',
  'Digimon':              'dgm',
  'Disney Lorcana':       'lorcana',
  'Dragon Ball':          'dragonball',
  'Riftbound':            'riftbound',
};

const CONDITION_LABELS = {
  MT: 'Mint',
  NM: 'Near Mint',
  EX: 'Excellent',
  GD: 'Good',
  LP: 'Light Played',
  PL: 'Played',
  PO: 'Poor',
};

const CONDITION_ORDER = ['Mint', 'Near Mint', 'Excellent', 'Good', 'Light Played', 'Played', 'Poor'];

const LANGUAGE_IDS = {
  'Englisch':                  '1',
  'Französisch':               '2',
  'Deutsch':                   '3',
  'Spanisch':                  '4',
  'Italienisch':               '5',
  'Chinesisch (vereinfacht)':  '6',
  'Japanisch':                 '7',
  'Portugiesisch':             '8',
  'Russisch':                  '9',
  'Koreanisch':                '10',
  'Chinesisch (traditionell)': '11',
};

// ── Hilfsfunktionen ───────────────────────────────────────

function extractNumericId(gid) {
  return parseInt(gid.split('/').pop());
}

function conditionAllowed(varConditionLabel, selectedCondition) {
  if (!selectedCondition) return true;
  const varIdx      = CONDITION_ORDER.indexOf(varConditionLabel);
  const selectedIdx = CONDITION_ORDER.indexOf(selectedCondition);
  if (varIdx === -1 || selectedIdx === -1) return true;
  return varIdx <= selectedIdx;
}

function languageLabel(langId) {
  return Object.entries(LANGUAGE_IDS).find(([, id]) => id === langId)?.[0] || langId;
}

async function storefrontGql(query, variables = {}) {
  const res = await fetch(STOREFRONT_URL, {
    method: 'POST',
    headers: {
      'Content-Type':                      'application/json',
      'X-Shopify-Storefront-Access-Token': STOREFRONT_TOKEN,
    },
    body: JSON.stringify({ query, variables }),
  });
  const data = await res.json();
  if (data.errors) throw new Error(JSON.stringify(data.errors));
  return data.data;
}

function parseDecklist(text) {
  const lines  = text.trim().split('\n');
  const result = [];

  for (const line of lines) {
    const trimmed = line.trim();
    if (!trimmed || trimmed.startsWith('//') || trimmed.startsWith('#')) continue;

    const match = trimmed.match(/^(\d+)x?\s+(.+)$/i);
    if (match) {
      result.push({
        quantity: parseInt(match[1]),
        name:     match[2].trim(),
      });
    }
  }
  return result;
}

// ── Mengenlogik ───────────────────────────────────────────

function allocateQuantity(requested, variants) {
  const sorted     = [...variants].sort((a, b) => a.price - b.price);
  const allocation = [];
  let remaining    = requested;

  for (const v of sorted) {
    if (remaining <= 0) break;
    const take = Math.min(remaining, v.available);
    if (take > 0) {
      allocation.push({ ...v, allocatedQty: take });
      remaining -= take;
    }
  }

  const fulfilled = requested - remaining;
  return {
    allocation,
    fulfilled,
    requested,
    complete:    remaining === 0,
    partial:     remaining > 0 && fulfilled > 0,
    unavailable: fulfilled === 0,
  };
}

// ── Produkte suchen ───────────────────────────────────────

async function fetchVariants(cardName, gameTag) {
  const gqlQuery = `
    query searchCard($query: String!) {
      products(first: 250, query: $query, sortKey: BEST_SELLING) {
        edges {
          node {
            id
            title
            handle
            featuredImage { url }
            variants(first: 50) {
              edges {
                node {
                  id
                  title
                  availableForSale
                  quantityAvailable
                  price { amount currencyCode }
                  selectedOptions { name value }
                  condition:  metafield(namespace: "cardmarket", key: "condition")  { value }
                  idLanguage: metafield(namespace: "cardmarket", key: "idLanguage") { value }
                }
              }
            }
          }
        }
      }
    }
  `;

  const searchQuery = `tag:${gameTag} product_type:Einzelkarte metafield:singles.name_en:${cardName}`;
  const data        = await storefrontGql(gqlQuery, { query: searchQuery });
  const products    = data?.products?.edges || [];

  const variants = [];

  for (const edge of products) {
    const product = edge.node;

    for (const varEdge of product.variants.edges) {
      const variant = varEdge.node;
      if (!variant.availableForSale || variant.quantityAvailable <= 0) continue;

      const varConditionCode  = variant.condition?.value  || '';
      const varLanguageId     = variant.idLanguage?.value || '';
      const varConditionLabel = CONDITION_LABELS[varConditionCode] || varConditionCode;

      const optionValues = variant.selectedOptions
        .filter(o => o.value !== 'Default Title')
        .map(o => o.value)
        .join(' / ');

      variants.push({
        productId:        product.id,
        productTitle:     product.title,
        handle:           product.handle,
        image:            product.featuredImage?.url || '',
        variantId:        variant.id,
        variantIdNumeric: extractNumericId(variant.id),
        variantTitle:     variant.title,
        price:            parseFloat(variant.price.amount),
        currency:         variant.price.currencyCode,
        condition:        varConditionLabel,
        languageId:       varLanguageId,
        available:        variant.quantityAvailable,
        optionValues,
      });
    }
  }

  return variants;
}

async function searchCard(cardName, gameTag, language, condition, langMode) {
  const selectedLangId = LANGUAGE_IDS[language] || '';
  const allVariants    = await fetchVariants(cardName, gameTag);

  const condFiltered = allVariants.filter(v => conditionAllowed(v.condition, condition));

  let langFiltered;
  if (!selectedLangId) {
    langFiltered = condFiltered;
  } else if (langMode === 'strict') {
    langFiltered = condFiltered.filter(v => v.languageId === selectedLangId);
  } else {
    const preferred = condFiltered.filter(v => v.languageId === selectedLangId);
    langFiltered    = preferred.length > 0 ? preferred : condFiltered;
  }

  return langFiltered.sort((a, b) => a.price - b.price);
}

// ── Cart API ──────────────────────────────────────────────

async function addToCart(lines) {
  const items = lines.map(l => ({
    id:       l.variantIdNumeric,
    quantity: l.quantity,
  }));

  const res = await fetch(window.Shopify.routes.root + 'cart/add.js', {
    method:  'POST',
    headers: { 'Content-Type': 'application/json' },
    body:    JSON.stringify({ items }),
  });

  const data = await res.json();
  return { ok: res.ok, data };
}

async function handleAddToCart(redirect = false) {
  const lines = searchResults
    .flatMap(r => r.allocation.allocation)
    .filter(a => a.allocatedQty > 0)
    .map(a => ({
      variantId:        a.variantId,
      variantIdNumeric: a.variantIdNumeric,
      quantity:         a.allocatedQty,
    }));

  if (lines.length === 0) {
    alert('Keine Karten zum Hinzufügen.');
    return;
  }

  document.querySelectorAll('.me-add-btn').forEach(btn => {
    btn.disabled    = true;
    btn.textContent = '⏳ Wird hinzugefügt...';
  });

  try {
    const result = await addToCart(lines);

    if (result.ok) {
      if (redirect) {
        window.location.href = '/cart';
      } else {
        document.dispatchEvent(new CustomEvent('dispatch:cart-drawer:refresh'));
        document.dispatchEvent(new CustomEvent('dispatch:cart-drawer:open'));
        const total = lines.reduce((s, l) => s + l.quantity, 0);
        document.querySelectorAll('.me-add-btn').forEach(btn => {
          btn.textContent = `✅ ${total} Artikel hinzugefügt`;
          btn.classList.add('me-btn-done');
        });
      }
    } else {
      const errMsg = result.data?.description || result.data?.message || 'Unbekannter Fehler';
      alert('Fehler: ' + errMsg);
      document.querySelectorAll('.me-add-btn').forEach(btn => {
        btn.disabled    = false;
        btn.textContent = btn.dataset.redirect === 'true'
          ? '🛒 Zum Warenkorb'
          : '🛒 Alle in den Warenkorb';
      });
    }
  } catch (err) {
    alert('Fehler: ' + err.message);
    document.querySelectorAll('.me-add-btn').forEach(btn => {
      btn.disabled    = false;
      btn.textContent = btn.dataset.redirect === 'true'
        ? '🛒 Zum Warenkorb'
        : '🛒 Alle in den Warenkorb';
    });
  }
}

// ── UI aufbauen ───────────────────────────────────────────

const app = document.getElementById('mass-entry-app');

app.innerHTML = `
  <div class="me-container">
    <h1 class="me-title">Deckliste importieren</h1>
    <p class="me-subtitle">
      Füge eine Deckliste ein — eine Karte pro Zeile, Menge zuerst.<br>
      Beispiel: <code>4 Lightning Bolt</code>
    </p>

    <div class="me-filters">
      <label class="me-filter-label">
        Spiel
        <select id="me-game">
          ${Object.keys(GAME_TAGS).map(g =>
            `<option value="${GAME_TAGS[g]}">${g}</option>`
          ).join('')}
        </select>
      </label>

      <label class="me-filter-label">
        Sprache
        <select id="me-language">
          <option value="Englisch">Englisch</option>
          <option value="Deutsch">Deutsch</option>
          <option value="Französisch">Französisch</option>
          <option value="Spanisch">Spanisch</option>
          <option value="Italienisch">Italienisch</option>
          <option value="Japanisch">Japanisch</option>
          <option value="Portugiesisch">Portugiesisch</option>
          <option value="Koreanisch">Koreanisch</option>
          <option value="Russisch">Russisch</option>
        </select>
      </label>

      <label class="me-filter-label">
        Sprachfilter
        <select id="me-lang-mode">
          <option value="fallback">Zielsprache bevorzugt, sonst andere</option>
          <option value="strict">Nur Zielsprache</option>
        </select>
      </label>

      <label class="me-filter-label">
        Zustand (mindestens)
        <select id="me-condition">
          <option value="">Beliebig</option>
          <option value="Mint">Mint</option>
          <option value="Near Mint">Near Mint</option>
          <option value="Excellent">Excellent</option>
          <option value="Good">Good</option>
          <option value="Light Played">Light Played</option>
          <option value="Played">Played</option>
          <option value="Poor">Poor</option>
        </select>
      </label>
    </div>

    <div class="me-input-area">
      <textarea
        id="me-decklist"
        placeholder="4 Lightning Bolt&#10;2 Counterspell&#10;1 Sol Ring"
        rows="10"
      ></textarea>
      <button id="me-search-btn" class="me-btn me-btn-primary">
        🔍 Suchen
      </button>
    </div>

    <div id="me-loading" class="me-loading" style="display:none">
      <div class="me-spinner"></div>
      <span id="me-loading-text">Suche läuft...</span>
    </div>

    <div id="me-results" style="display:none">
      <div class="me-results-header">
        <div class="me-results-summary">
          <span id="me-found-count"></span>
          <span id="me-total-price"></span>
        </div>
        <button class="me-btn me-btn-success me-add-btn" data-redirect="false">
          🛒 Alle in den Warenkorb
        </button>
      </div>
      <div id="me-results-list"></div>
      <div class="me-results-footer">
        <span id="me-total-price-bottom"></span>
        <button class="me-btn me-btn-success me-add-btn" data-redirect="true">
          🛒 Zum Warenkorb
        </button>
      </div>
    </div>
  </div>
`;

// ── State ─────────────────────────────────────────────────

let searchResults = [];

// ── Element Referenzen ────────────────────────────────────

const searchBtn   = document.getElementById('me-search-btn');
const loadingEl   = document.getElementById('me-loading');
const loadingText = document.getElementById('me-loading-text');
const resultsEl   = document.getElementById('me-results');
const resultsList = document.getElementById('me-results-list');

// ── Suche ─────────────────────────────────────────────────

searchBtn.addEventListener('click', async () => {
  const decklist  = document.getElementById('me-decklist').value;
  const gameTag   = document.getElementById('me-game').value;
  const language  = document.getElementById('me-language').value;
  const langMode  = document.getElementById('me-lang-mode').value;
  const condition = document.getElementById('me-condition').value;

  const cards = parseDecklist(decklist);
  if (cards.length === 0) {
    alert('Bitte gib eine Deckliste ein.');
    return;
  }

  searchResults            = [];
  loadingEl.style.display  = 'flex';
  resultsEl.style.display  = 'none';
  resultsList.innerHTML    = '';
  searchBtn.disabled       = true;

  for (let i = 0; i < cards.length; i++) {
    const card = cards[i];
    loadingText.textContent = `Suche ${i + 1} / ${cards.length}: ${card.name}`;

    try {
      const variants   = await searchCard(card.name, gameTag, language, condition || null, langMode);
      const allocation = allocateQuantity(card.quantity, variants);

      searchResults.push({
        name:      card.name,
        requested: card.quantity,
        variants,
        allocation,
      });
    } catch (err) {
      searchResults.push({
        name:      card.name,
        requested: card.quantity,
        variants:  [],
        allocation: {
          allocation: [], fulfilled: 0, requested: card.quantity,
          complete: false, partial: false, unavailable: true,
        },
        error: err.message,
      });
    }

    await new Promise(r => setTimeout(r, 150));
  }

  loadingEl.style.display = 'none';
  searchBtn.disabled      = false;
  renderResults();
});

// ── Karten rendern ────────────────────────────────────────

function renderCard(result) {
  const alloc = result.allocation;

  if (alloc.unavailable) {
    return `
      <div class="me-card me-card-notfound">
        <div class="me-card-info">
          <div class="me-card-name">${result.requested}× ${result.name}</div>
          <div class="me-status-error">❌ Nicht im Shop verfügbar</div>
        </div>
      </div>
    `;
  }

  const statusLabel = alloc.complete
    ? `<span class="me-status-ok">✅ ${alloc.fulfilled}/${alloc.requested} verfügbar</span>`
    : `<span class="me-status-partial">⚠️ Nur ${alloc.fulfilled}/${alloc.requested} verfügbar</span>`;

  const allocationRows = alloc.allocation.map(a => `
    <div class="me-allocation-row">
      <a href="/products/${a.handle}" target="_blank" class="me-allocation-title">
        ${a.productTitle}
      </a>
      <span class="me-allocation-meta">
        ${a.optionValues || `${a.condition} · ${languageLabel(a.languageId)}`}
      </span>
      <div class="me-allocation-right">
        <span class="me-allocation-price">€${a.price.toFixed(2)} × ${a.allocatedQty}</span>
        <span class="me-allocation-subtotal">= €${(a.price * a.allocatedQty).toFixed(2)}</span>
      </div>
    </div>
  `).join('');

  const image     = alloc.allocation[0]?.image || '';
  const cardTotal = alloc.allocation.reduce((s, a) => s + a.price * a.allocatedQty, 0);

  return `
    <div class="me-card ${alloc.partial ? 'me-card-partial' : ''}">
      ${image ? `
        <a href="/products/${alloc.allocation[0].handle}" target="_blank">
          <img class="me-card-img" src="${image}" alt="${result.name}"
            onerror="this.style.display='none'">
        </a>` : ''}
      <div class="me-card-info">
        <div class="me-card-name">${result.name}</div>
        <div class="me-card-allocations">${allocationRows}</div>
      </div>
      <div class="me-card-right">
        <div class="me-card-total">€${cardTotal.toFixed(2)}</div>
        ${statusLabel}
      </div>
    </div>
  `;
}

function renderGroup(items, label, className) {
  if (items.length === 0) return '';
  return `
    <div class="me-group-header ${className}">
      <span class="me-group-label">${label}</span>
      <span class="me-group-count">${items.length} Karte${items.length !== 1 ? 'n' : ''}</span>
    </div>
    ${items.map(renderCard).join('')}
  `;
}

// ── Ergebnisse rendern ────────────────────────────────────

function getTotalPrice() {
  return searchResults.reduce((sum, r) =>
    sum + r.allocation.allocation.reduce((s, a) =>
      s + a.price * a.allocatedQty, 0
    ), 0
  );
}

function updateTotalPrices() {
  const total = getTotalPrice();
  const text  = `Gesamt: €${total.toFixed(2)}`;
  const topEl    = document.getElementById('me-total-price');
  const bottomEl = document.getElementById('me-total-price-bottom');
  if (topEl)    topEl.textContent    = text;
  if (bottomEl) bottomEl.textContent = text;
}

function renderResults() {
  const completeItems  = searchResults.filter(r => r.allocation.complete);
  const partialItems   = searchResults.filter(r => r.allocation.partial);
  const unavailItems   = searchResults.filter(r => r.allocation.unavailable);

  document.getElementById('me-found-count').textContent =
    `✅ ${completeItems.length} vollständig · ⚠️ ${partialItems.length} teilweise · ❌ ${unavailItems.length} nicht verfügbar`;

  resultsList.innerHTML = [
    renderGroup(completeItems, '✅ Vollständig verfügbar',   'me-group-complete'),
    renderGroup(partialItems,  '⚠️ Teilweise verfügbar',    'me-group-partial'),
    renderGroup(unavailItems,  '❌ Nicht verfügbar',         'me-group-unavail'),
  ].join('');

  resultsEl.style.display = 'block';
  updateTotalPrices();
}

// ── Button Event Listener ─────────────────────────────────

document.addEventListener('click', e => {
  if (!e.target.classList.contains('me-add-btn')) return;
  const redirect = e.target.dataset.redirect === 'true';
  handleAddToCart(redirect);
});